import React from "react";
import { useTranslation } from "react-i18next";
import Icon from "../../components/AppIcon";
import { cn } from "../../utils/cn";
import ModuleBrochure, {
  type BrochureFeature,
  type BrochureMetric,
  type BrochureUseCase,
} from "./ModuleBrochure";

/**
 * Payroll brochure — Phase 6 build.
 *
 * Tone: "closes on Friday, not Monday". Pitch is calibrated to the payroll
 * lead who lives in spreadsheets the week before WPS submission, and the
 * CFO who wants the whole cycle in three numbers.
 *
 * Illustration: a synthetic payslip card with a fat checkmark stamp, a
 * "WPS-ready" badge, and a floating "Closed in 2 days" accent pill. Built
 * from inline SVG atmosphere + Tailwind-composed device frame.
 *
 * Mockup: a faux payroll table — 4 employees with gross, deductions, net
 * pay, and a status pill (Posted/Pending). A "This cycle" stat card sits
 * in the right sidebar.
 */

// ----- Illustration: payslip + checkmark stamp + WPS-ready badge -----

const PayrollIllustration: React.FC = () => {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.dir() === "rtl";

  return (
    <div className="relative aspect-[4/5] w-full">
      {/* Soft atmospheric backdrop — grid + violet wash. */}
      <svg
        viewBox="0 0 320 400"
        className="absolute inset-0 h-full w-full"
        aria-hidden="true"
      >
        <defs>
          <pattern id="pay-grid" width="24" height="24" patternUnits="userSpaceOnUse">
            <path
              d="M 24 0 L 0 0 0 24"
              fill="none"
              stroke="rgb(124 58 237 / 0.08)"
              strokeWidth="1"
            />
          </pattern>
          <radialGradient id="pay-wash" cx="50%" cy="50%" r="50%">
            <stop offset="50%" stopColor="rgb(124 58 237 / 0.0)" />
            <stop offset="100%" stopColor="rgb(124 58 237 / 0.14)" />
          </radialGradient>
        </defs>
        <rect width="320" height="400" fill="url(#pay-grid)" />
        <circle cx="160" cy="200" r="160" fill="url(#pay-wash)" />
      </svg>

      {/* Payslip card — drawn as nested divs so Tailwind composes onto it. */}
      <div className="absolute left-1/2 top-1/2 h-[78%] w-[64%] -translate-x-1/2 -translate-y-1/2 -rotate-2 rounded-2xl border border-slate-200 bg-white p-4 shadow-[0_24px_60px_-20px_rgba(15,23,42,0.35)]">
        {/* Slip header */}
        <div className="flex items-start justify-between border-b border-dashed border-slate-200 pb-2.5">
          <div>
            <p className="text-[9px] font-semibold uppercase tracking-wider text-violet-700">
              {t("brochure.payroll.illustration.payslip")}
            </p>
            <p className="mt-0.5 text-[10px] font-semibold text-slate-900">
              {t("brochure.payroll.illustration.payslipMonth")}
            </p>
          </div>
          <span className="rounded-md bg-violet-50 px-1.5 py-0.5 text-[9px] font-semibold text-violet-800 ring-1 ring-violet-200">
            #4291
          </span>
        </div>

        {/* Line items */}
        <ul className="mt-3 space-y-1.5 text-[10px]">
          <li className="flex items-center justify-between">
            <span className="text-slate-500">
              {t("brochure.payroll.illustration.line.basic")}
            </span>
            <span className="font-semibold tabular-nums text-slate-900">12,000</span>
          </li>
          <li className="flex items-center justify-between">
            <span className="text-slate-500">
              {t("brochure.payroll.illustration.line.allowance")}
            </span>
            <span className="font-semibold tabular-nums text-slate-900">2,500</span>
          </li>
          <li className="flex items-center justify-between">
            <span className="text-slate-500">
              {t("brochure.payroll.illustration.line.deductions")}
            </span>
            <span className="font-semibold tabular-nums text-rose-600">-850</span>
          </li>
        </ul>

        {/* Net pay band */}
        <div className="mt-3 rounded-lg bg-violet-50 px-2.5 py-2 ring-1 ring-violet-200">
          <p className="text-[9px] font-semibold uppercase tracking-wider text-violet-700">
            {t("brochure.payroll.illustration.netPay")}
          </p>
          <p className="mt-0.5 text-base font-semibold tabular-nums text-violet-900">
            13,650 <span className="text-[9px] font-medium text-violet-700">AED</span>
          </p>
        </div>

        {/* WPS-ready badge */}
        <div className="mt-3 flex items-center gap-1.5 rounded-md border border-emerald-200 bg-emerald-50 px-2 py-1">
          <Icon name="ShieldCheck" size={12} className="text-emerald-700" aria-hidden="true" />
          <span className="text-[9px] font-semibold text-emerald-800">
            {t("brochure.payroll.illustration.wpsBadge")}
          </span>
        </div>
      </div>

      {/* Checkmark stamp — angled, top-end corner. */}
      <div
        className={cn(
          "absolute flex h-16 w-16 items-center justify-center rounded-full bg-emerald-500 text-white shadow-[0_18px_36px_-10px_rgba(16,185,129,0.6)]",
          "rotate-12 ring-4 ring-white",
          isRtl ? "left-[4%] top-[8%]" : "right-[4%] top-[8%]",
        )}
        aria-hidden="true"
      >
        <Icon name="BadgeCheck" size={28} />
      </div>

      {/* Floating accent pill — "Closed in 2 days". */}
      <div
        className={cn(
          "absolute bottom-[8%] flex items-center gap-1.5 rounded-full bg-violet-600 px-3 py-1.5",
          "text-[11px] font-semibold text-white shadow-[0_10px_24px_-8px_rgba(124,58,237,0.55)]",
          isRtl ? "left-[6%]" : "right-[6%]",
        )}
      >
        <Icon name="Sparkles" size={12} aria-hidden="true" />
        <span>{t("brochure.payroll.illustration.closedPill")}</span>
      </div>
    </div>
  );
};

// ----- Mockup: faux payroll table -----

type PayStatus = "posted" | "pending";

interface PayRow {
  initials: string;
  nameKey: string;
  roleKey: string;
  tint: string;
  gross: string;
  deductions: string;
  net: string;
  status: PayStatus;
}

const PAY_ROWS: PayRow[] = [
  {
    initials: "RA",
    nameKey: "brochure.payroll.mockup.person.reem",
    roleKey: "brochure.payroll.mockup.role.reception",
    tint: "bg-violet-100 text-violet-700",
    gross: "9,500",
    deductions: "-420",
    net: "9,080",
    status: "posted",
  },
  {
    initials: "HK",
    nameKey: "brochure.payroll.mockup.person.hassan",
    roleKey: "brochure.payroll.mockup.role.fieldTech",
    tint: "bg-emerald-100 text-emerald-700",
    gross: "11,200",
    deductions: "-560",
    net: "10,640",
    status: "posted",
  },
  {
    initials: "SM",
    nameKey: "brochure.payroll.mockup.person.sara",
    roleKey: "brochure.payroll.mockup.role.accounts",
    tint: "bg-amber-100 text-amber-800",
    gross: "14,500",
    deductions: "-1,200",
    net: "13,300",
    status: "pending",
  },
  {
    initials: "OF",
    nameKey: "brochure.payroll.mockup.person.omar",
    roleKey: "brochure.payroll.mockup.role.warehouse",
    tint: "bg-sky-100 text-sky-700",
    gross: "8,800",
    deductions: "-380",
    net: "8,420",
    status: "posted",
  },
];

const PayrollMockup: React.FC = () => {
  const { t } = useTranslation();
  return (
    <div className="grid gap-4 lg:grid-cols-[1fr_220px]">
      {/* Payroll table */}
      <div className="overflow-hidden rounded-lg border border-slate-200">
        <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-4 py-2.5">
          <div className="flex items-center gap-2">
            <Icon name="Wallet" size={14} className="text-slate-500" aria-hidden="true" />
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-600">
              {t("brochure.payroll.mockup.heading")}
            </p>
          </div>
          <p className="text-[10px] font-medium text-slate-400">
            {t("brochure.payroll.mockup.cycle")}
          </p>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-white">
                <th className="w-44 px-3 py-2 text-start text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                  {t("brochure.payroll.mockup.col.employee")}
                </th>
                <th className="px-2 py-2 text-end text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                  {t("brochure.payroll.mockup.col.gross")}
                </th>
                <th className="px-2 py-2 text-end text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                  {t("brochure.payroll.mockup.col.deductions")}
                </th>
                <th className="px-2 py-2 text-end text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                  {t("brochure.payroll.mockup.col.net")}
                </th>
                <th className="px-2 py-2 text-center text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                  {t("brochure.payroll.mockup.col.status")}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {PAY_ROWS.map((r) => (
                <tr key={r.initials} className="bg-white">
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-2.5">
                      <span
                        className={cn(
                          "inline-flex h-7 w-7 items-center justify-center rounded-full text-[10px] font-semibold",
                          r.tint,
                        )}
                      >
                        {r.initials}
                      </span>
                      <div className="leading-tight">
                        <p className="text-xs font-semibold text-slate-900">{t(r.nameKey)}</p>
                        <p className="text-[10px] text-slate-500">{t(r.roleKey)}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-2 py-2.5 text-end text-xs font-semibold tabular-nums text-slate-900">
                    {r.gross}
                  </td>
                  <td className="px-2 py-2.5 text-end text-xs font-semibold tabular-nums text-rose-600">
                    {r.deductions}
                  </td>
                  <td className="px-2 py-2.5 text-end text-xs font-semibold tabular-nums text-slate-900">
                    {r.net}
                  </td>
                  <td className="px-2 py-2.5 text-center">
                    <span
                      className={cn(
                        "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ring-1",
                        r.status === "posted"
                          ? "bg-emerald-50 text-emerald-700 ring-emerald-200"
                          : "bg-amber-50 text-amber-800 ring-amber-200",
                      )}
                    >
                      <span
                        className={cn(
                          "h-1.5 w-1.5 rounded-full",
                          r.status === "posted" ? "bg-emerald-500" : "bg-amber-500",
                        )}
                      />
                      {r.status === "posted"
                        ? t("brochure.payroll.mockup.status.posted")
                        : t("brochure.payroll.mockup.status.pending")}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* This cycle stat card */}
      <div className="grid gap-3">
        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
            {t("brochure.payroll.mockup.thisCycle.heading")}
          </p>
          <div className="mt-2 space-y-2 text-xs">
            <div className="flex items-center justify-between">
              <span className="text-slate-600">
                {t("brochure.payroll.mockup.thisCycle.gross")}
              </span>
              <span className="font-semibold tabular-nums text-slate-900">44,000</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-slate-600">
                {t("brochure.payroll.mockup.thisCycle.net")}
              </span>
              <span className="font-semibold tabular-nums text-slate-900">41,440</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-slate-600">
                {t("brochure.payroll.mockup.thisCycle.employees")}
              </span>
              <span className="font-semibold tabular-nums text-slate-900">42</span>
            </div>
          </div>
        </div>
        <div className="rounded-lg border border-violet-200 bg-violet-50 p-4">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-violet-800">
            {t("brochure.payroll.mockup.wps.heading")}
          </p>
          <p className="mt-1.5 text-xs leading-snug text-violet-900/80">
            {t("brochure.payroll.mockup.wps.hint")}
          </p>
          <span className="mt-2 inline-flex items-center gap-1 text-[10px] font-semibold text-violet-900">
            <Icon name="ShieldCheck" size={12} aria-hidden="true" />
            {t("brochure.payroll.mockup.wps.cta")}
          </span>
        </div>
      </div>
    </div>
  );
};

// ----- Brochure props ------

const PayrollBrochure: React.FC = () => {
  const { t } = useTranslation();

  const features: BrochureFeature[] = [
    {
      icon: "ShieldCheck",
      title: t("brochure.payroll.features.compliance.title"),
      description: t("brochure.payroll.features.compliance.description"),
    },
    {
      icon: "Receipt",
      title: t("brochure.payroll.features.claims.title"),
      description: t("brochure.payroll.features.claims.description"),
    },
    {
      icon: "HandCoins",
      title: t("brochure.payroll.features.benefits.title"),
      description: t("brochure.payroll.features.benefits.description"),
    },
    {
      icon: "BadgeCheck",
      title: t("brochure.payroll.features.eos.title"),
      description: t("brochure.payroll.features.eos.description"),
    },
    {
      icon: "Coins",
      title: t("brochure.payroll.features.variable.title"),
      description: t("brochure.payroll.features.variable.description"),
    },
    {
      icon: "FileSpreadsheet",
      title: t("brochure.payroll.features.reports.title"),
      description: t("brochure.payroll.features.reports.description"),
    },
  ];

  const metrics: BrochureMetric[] = [
    {
      value: t("brochure.payroll.metrics.daysValue"),
      label: t("brochure.payroll.metrics.daysLabel"),
    },
    {
      value: "100%",
      label: t("brochure.payroll.metrics.complianceLabel"),
    },
    {
      value: "0",
      label: t("brochure.payroll.metrics.correctionsLabel"),
    },
  ];

  const useCases: BrochureUseCase[] = [
    {
      persona: t("brochure.payroll.useCases.lead.persona"),
      scenario: t("brochure.payroll.useCases.lead.scenario"),
      outcome: t("brochure.payroll.useCases.lead.outcome"),
    },
    {
      persona: t("brochure.payroll.useCases.cfo.persona"),
      scenario: t("brochure.payroll.useCases.cfo.scenario"),
      outcome: t("brochure.payroll.useCases.cfo.outcome"),
    },
  ];

  return (
    <ModuleBrochure
      slug="payroll"
      hero={{
        eyebrow: t("brochure.payroll.hero.eyebrow"),
        headline: t("brochure.payroll.hero.headline"),
        subheadline: t("brochure.payroll.hero.subheadline"),
        illustration: <PayrollIllustration />,
      }}
      features={features}
      metrics={metrics}
      mockup={{
        title: t("brochure.payroll.mockup.caption"),
        content: <PayrollMockup />,
      }}
      useCases={useCases}
    />
  );
};

export default PayrollBrochure;
